// src/components/Boxes/Overview/overview.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import OverviewTransactions from "./overviewTransactions.component.jsx"; // adjust path if needed
import OverviewNotes from "./overviewNotes.component.jsx"; // ✅ NEW
import OverviewReports from "./overviewReports.component.jsx"; // ✅ NEW
import OverviewFiles from "./overviewFiles.component.jsx";

const STORAGE_KEY_POS = "OVERVIEW_BOX_LOCATION";
const DEFAULT_POS = { x: window.innerWidth - 1220 - 10, y: 100 };

const Overview = () => {
    const playerCtx = usePlayer?.() || {};
    const player = playerCtx.activePlayer || playerCtx.player || playerCtx.selectedPlayer || {};
    const p = player || {};

    const [nums, setNums] = useState({
        reports: 0,
        notes: 0,
        transactions: 0,
        quick: 0,
    });
    const [loadingNums, setLoadingNums] = useState(false);

    // "" | "Transactions" | "Reports" | "Notes" | "Files"
    const [whatButton, setWhatButton] = useState("");

    const toggleWhatButton = (key) => {
        setWhatButton((prev) => (prev === key ? "" : key));
    };

    // --------------------- counts fetch (already working) ---------------------
    useEffect(() => {
        const controller = new AbortController();

        const fetchPlayerNumbers = async () => {
            setNums({ reports: 0, notes: 0, transactions: 0, quick: 0 });

            if (!p?.FBXID) {
                setLoadingNums(false);
                return;
            }

            setLoadingNums(true);

            const token = await getUserFromToken().catch(() => null);
            if (!token?.team) {
                setLoadingNums(false);
                return;
            }

            try {
                const res = await fetch("https://www.fbxchange.com/ReactPHP/overview.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: controller.signal,
                    body: JSON.stringify({
                        FBXID: p.FBXID,
                        myTeam: token.team,
                        scout: token.scout,
                        script: "getPlayerNumbers",
                    }),
                });

                const raw = await res.text();
                let data;
                try {
                    data = JSON.parse(raw);
                } catch (e) {
                    console.error("overview.php getPlayerNumbers not JSON:", raw);
                    setLoadingNums(false);
                    return;
                }

                setNums({
                    reports: Number(data?.reports ?? 0),
                    notes: Number(data?.notes ?? 0),
                    transactions: Number(data?.transactions ?? 0),
                    quick: Number(data?.quick ?? 0),
                });

                setLoadingNums(false);
            } catch (err) {
                if (err?.name === "AbortError") return;
                console.error("Error fetching overview numbers:", err);
                setLoadingNums(false);
            }
        };

        fetchPlayerNumbers();
        return () => controller.abort();
    }, [p?.FBXID]);

    // --------------------- helpers ---------------------

    const cleanPart = (v) => {
        if (v === undefined || v === null) return "";
        const s = String(v).trim();
        if (!s) return "";

        const low = s.toLowerCase();
        if (low === "null" || low === "undefined" || low === "na" || low === "n/a") return "";
        if (s === "0000-00-00" || s === "1950-01-01") return "";
        return s;
    };

    const fmtDate = (d) => {
        if (!d) return "";
        const s = String(d).trim();
        if (s === "1950-01-01" || s === "0000-00-00" || s === "null") return "";
        return s;
    };

    // "5040 X 2025-05-28"
    const fmtHws = (valIn, vIn, dIn) => {
        const val = valIn === undefined || valIn === null ? "" : String(valIn).trim();
        if (!val) return "-";
        const v = vIn === undefined || vIn === null ? "" : String(vIn).trim();
        const dt = fmtDate(dIn);

        let out = val;
        if (v) out += ` ${v}`;
        if (dt) out += ` ${dt}`;
        return out;
    };

    const fmtSpeedVal = (s) => {
        if (s === undefined || s === null || String(s).trim() === "") return "";
        const n = Number(s);
        if (Number.isNaN(n)) return String(s).trim();
        return n.toFixed(2);
    };

    // --------------------- derived fields ---------------------
    const nameLine = useMemo(() => {
        if (p.NAME) return String(p.NAME);
        const last = p.LAST || p.last || p.lastName || "";
        const first = p.FIRST || p.first || p.firstName || "";
        const built = [last, first].filter(Boolean).join(", ");
        return built || "-";
    }, [p]);

    const cpLine = useMemo(() => String(p.CP || p.COL_POS1 || p.colPos1 || p.position || "-"), [p]);

    const ageLine = useMemo(() => {
        const a = p.AGE ?? p.age;
        return a !== undefined && a !== null && String(a).trim() !== "" ? String(a) : "-";
    }, [p]);

    const schoolLine = useMemo(() => {
        const longName = p.longSchool || "";
        const nickname = p.nickname || "";

        if (longName && nickname) {
            return `${longName} (${nickname})`;
        } else if (longName) {
            return String(longName);
        } else if (nickname) {
            return String(nickname);
        } else {
            return "-";
        }
    }, [p]);

    const heightLine = useMemo(
        () => fmtHws(p.height, p.heightV, p.heightDate),
        [p]
    );

    const weightLine = useMemo(
        () => fmtHws(p.weight, p.weightV, p.weightDate),
        [p]
    );

    const speedLine = useMemo(() => {
        const val = fmtSpeedVal(p.speed40Fastest);
        const v = p.speed40FVerified || "";
        const d = p.speed40FDate || "";
        return fmtHws(val, v, d);
    }, [p]);

    const cflLine = useMemo(() => {
        const parts = [
            cleanPart(p.cflTeam),
            cleanPart(p.cflStatus),
            cleanPart(fmtDate(p.cflDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.cflStatus, p.cflTeam, p.cflDate]);

    const nflLine = useMemo(() => {
        const parts = [
            cleanPart(p.nflTeam),
            cleanPart(p.nflStatus),
            cleanPart(fmtDate(p.nflDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.nflStatus, p.nflTeam, p.nflDate]);

    const uflLine = useMemo(() => {
        const parts = [
            cleanPart(p.ufl24Status),
            cleanPart(p.ufl24Team),
            cleanPart(fmtDate(p.ufl24Date)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.ufl24Status, p.ufl24Team, p.ufl24Date]);

    const draftLine = useMemo(() => {
        const yr = p.DRAFT_YEAR ?? p.nflDraftYear ?? "";
        const tm = p.DRAFTTEAM ?? p.nflDraftTeam ?? "";
        const rd = p.nflDraftRound ? `Round: ${p.nflDraftRound}` : "";
        const parts = [yr, tm, rd].filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
        return parts.length ? parts.join(" - ") : "-";
    }, [p]);

    const cfldraftLine = useMemo(() => {
        const yr = p.cflDraftYear ?? "";
        const tm = p.cflDraftTeam ?? "";
        const rd = p.cflDraftRound ? `Round: ${p.cflDraftRound}` : "";
        const parts = [yr, tm, rd].filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
        return parts.length ? parts.join(" - ") : "-";
    }, [p]);

    const eventLine = useMemo(() => {
        const parts = [
            cleanPart(p.EVENT || p.theEvent || p.event),
            cleanPart(fmtDate(p.EVENTDATE || p.theEventDate || p.eventDate)),
        ].filter(Boolean);

        return parts.length ? parts.join(" ") : "-";
    }, [p.EVENT, p.theEvent, p.event, p.EVENTDATE, p.theEventDate, p.eventDate]);

    const reportCount = nums.reports ?? 0;
    const notesCount = nums.notes ?? 0;
    const transCount = nums.transactions ?? 0;
    const quickCount = nums.quick ?? 0;
    const showFilesPanel = whatButton === "Files";
    const titleLine = `${nameLine} | CP: ${cpLine} | Age: ${ageLine} | ${schoolLine}`;

    // --------------------- EXPAND STYLE (keeps within DragBox) ---------------------
    const expanded =
        whatButton === "Transactions" ||
        whatButton === "Notes" ||
        whatButton === "Reports" ||
        showFilesPanel;

    const outerStyle = expanded
        ? { height: "720px", overflow: "hidden" }
        : { minHeight: 420 };

    return (
        <DragBox2026
            storageKey={STORAGE_KEY_POS}
            defaultPos={DEFAULT_POS}
            title={titleLine}
            countText=""
            minWidth={1220}
            maxWidth={1220}
            zIndex={500}
            showReset={true}
            wideTitle={true}
        >
            <div className="container-fluid p-0" style={outerStyle}>
                <div className="row g-0 h-100">
                    <div className="col-48 h-100">
                        <div className="bg-info-subtle rounded-3 p-3 h-100 d-flex flex-column">
                            {/* ===================== OVERVIEW MAIN CARD ===================== */}
                            <div className="bg-white rounded-2 border p-3">
                                {/* H/W/S */}
                                {!expanded && (
                                <div className="row g-2 border-bottom pb-2 mb-2">
                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Height: </span>{heightLine}
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Weight: </span>{weightLine}
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="small">
                                            <span className="text-primary fw-semibold">Speed: </span>{speedLine}
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* League Status Bars */}
                                {!expanded && (
                                <div className="row g-2 mb-2">
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">CFL</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(220,53,69,.20)" }}>
                                            <div className="small">{cflLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">NFL</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(13,110,253,.20)" }}>
                                            <div className="small">{nflLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">UFL 24</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(255,193,7,.25)" }}>
                                            <div className="small">{uflLine}</div>
                                        </div>
                                    </div>
                                </div>
                                )}

                
                                {!expanded && (
                                <div className="row g-2">
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">CFL DRAFT YR - TEAM - ROUND</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(220,53,69,.20)" }}>
                                            <div className="small">{cfldraftLine}</div>
                                        </div>
                                    </div>
                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">NFL DRAFT YR - TEAM - ROUND</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(13,110,253,.20)" }}>
                                            <div className="small">{draftLine}</div>
                                        </div>
                                    </div>

                                    <div className="col-16">
                                        <div className="text-primary small fw-semibold">EVENT</div>
                                        <div className="rounded-1 px-2 py-2" style={{ background: "rgba(111,66,193,.20)" }}>
                                            <div className="small">{eventLine}</div>
                                        </div>
                                    </div>
                                </div>
                                )}

                                {/* Buttons (Reports/Notes/Transactions toggle + ORANGE active) */}
                                <div className={`row g-2 ${expanded ? "mt-0" : "mt-2"}`}>
                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">SCOUT REPORTS</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Reports"
                                                ? "btn-warning"
                                                : (reportCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Reports")}
                                        >
                                            {loadingNums ? "REPORTS [...]" : `REPORTS [${reportCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">NOTES</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Notes"
                                                ? "btn-warning"
                                                : (notesCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Notes")}
                                        >
                                            {loadingNums ? "NOTES [...]" : `NOTES [${notesCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">TRANSACTIONS</div>
                                        <button
                                            type="button"
                                            className={`btn ${whatButton === "Transactions"
                                                ? "btn-warning"
                                                : (transCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Transactions")}
                                        >
                                            {loadingNums ? "TRANS [...]" : `TRANS [${transCount}]`}
                                        </button>
                                    </div>

                                    <div className="col-12">
                                        <div className="text-primary small fw-semibold mb-2">FILES</div>
                                        <button
                                            type="button"
                                            className={`btn ${showFilesPanel
                                                ? "btn-warning"
                                                : (quickCount > 0 ? "btn-success" : "btn-outline-secondary")
                                                } btn-sm w-100`}
                                            onClick={() => toggleWhatButton("Files")}
                                        >
                                            {loadingNums ? "FILES [...]" : `FILES [${quickCount}]`}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* ===================== END OVERVIEW MAIN CARD ===================== */}

	                            {/* ===================== EXPANDED NOTES PANEL ===================== */}
	                            {whatButton === "Notes" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewNotes FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED NOTES PANEL ===================== */}

	                            {/* ===================== EXPANDED TRANSACTIONS PANEL ===================== */}
	                            {whatButton === "Transactions" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewTransactions FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED TRANSACTIONS PANEL ===================== */}

	                            {/* ===================== EXPANDED REPORTS PANEL ===================== */}
	                            {whatButton === "Reports" ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "auto", overflowX: "hidden" }}>
                                        <OverviewReports FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED REPORTS PANEL ===================== */}

	                            {/* ===================== EXPANDED FILES PANEL ===================== */}
	                            {showFilesPanel ? (
	                                <div
	                                    className="mt-3 bg-white rounded-2 border d-flex flex-column flex-grow-1"
	                                    style={{ minHeight: 0, overflow: "hidden" }}
	                                >
                                    <div className="flex-grow-1" style={{ overflowY: "hidden", overflowX: "hidden" }}>
                                        <OverviewFiles FBXID={p?.FBXID} />
                                    </div>
                                </div>
                            ) : null}
                            {/* ===================== END EXPANDED FILES PANEL ===================== */}
                        </div>
                    </div>
                </div>
            </div>
        </DragBox2026>
    );
};

export default Overview;
